/**
 * ThresholdEditor Component
 *
 * Slider + number input for configuring warning and critical
 * quota alert thresholds (0-100%).
 */

import React, { useCallback } from 'react';
import { clsx } from 'clsx';

export interface ThresholdValues {
  warning_threshold: number;
  critical_threshold: number;
}

export interface ThresholdEditorProps {
  value: ThresholdValues;
  onChange: (value: ThresholdValues) => void;
  disabled?: boolean;
  className?: string;
}

export const ThresholdEditor: React.FC<ThresholdEditorProps> = ({
  value,
  onChange,
  disabled = false,
  className = '',
}) => {
  const warningPct = Math.round(value.warning_threshold * 100);
  const criticalPct = Math.round(value.critical_threshold * 100);

  const handleWarningChange = useCallback(
    (raw: number) => {
      const clamped = Math.min(99, Math.max(50, raw));
      onChange({
        ...value,
        warning_threshold: clamped / 100,
        critical_threshold: Math.max(value.critical_threshold, (clamped + 1) / 100),
      });
    },
    [value, onChange]
  );

  const handleCriticalChange = useCallback(
    (raw: number) => {
      const clamped = Math.min(100, Math.max(51, raw));
      onChange({
        ...value,
        critical_threshold: clamped / 100,
        warning_threshold: Math.min(value.warning_threshold, (clamped - 1) / 100),
      });
    },
    [value, onChange]
  );

  return (
    <div className={clsx('space-y-4', className)}>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          警告阈值 (%)
        </label>
        <div className="flex items-center gap-3">
          <input
            type="range"
            min={50}
            max={99}
            value={warningPct}
            onChange={(e) => handleWarningChange(Number(e.target.value))}
            disabled={disabled}
            className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-yellow-500 disabled:opacity-50"
          />
          <input
            type="number"
            min={50}
            max={99}
            value={warningPct}
            onChange={(e) => handleWarningChange(Number(e.target.value))}
            disabled={disabled}
            className="w-16 px-2 py-1 border border-gray-300 rounded text-center text-sm disabled:bg-gray-50"
          />
        </div>
        <p className="text-xs text-yellow-600 mt-1">
          配额使用率达到 {warningPct}% 时发送警告
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          严重阈值 (%)
        </label>
        <div className="flex items-center gap-3">
          <input
            type="range"
            min={51}
            max={100}
            value={criticalPct}
            onChange={(e) => handleCriticalChange(Number(e.target.value))}
            disabled={disabled}
            className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-red-500 disabled:opacity-50"
          />
          <input
            type="number"
            min={51}
            max={100}
            value={criticalPct}
            onChange={(e) => handleCriticalChange(Number(e.target.value))}
            disabled={disabled}
            className="w-16 px-2 py-1 border border-gray-300 rounded text-center text-sm disabled:bg-gray-50"
          />
        </div>
        <p className="text-xs text-red-600 mt-1">
          配额使用率达到 {criticalPct}% 时发送严重告警
        </p>
      </div>

      <div className="p-3 bg-gray-50 rounded-lg">
        <div className="text-xs font-medium text-gray-600 mb-2">阈值预览</div>
        <div className="h-3 bg-gray-200 rounded-full overflow-hidden relative">
          <div
            className="absolute left-0 top-0 h-full bg-gradient-to-r from-green-400 via-yellow-400 to-red-400"
            style={{ width: '100%' }}
          />
          <div
            className="absolute top-0 h-full w-0.5 bg-yellow-600"
            style={{ left: `${warningPct}%` }}
          />
          <div
            className="absolute top-0 h-full w-0.5 bg-red-600"
            style={{ left: `${criticalPct}%` }}
          />
        </div>
        <div className="flex justify-between text-xs text-gray-500 mt-1">
          <span>0%</span>
          <span className="text-yellow-600">{warningPct}% 警告</span>
          <span className="text-red-600">{criticalPct}% 严重</span>
        </div>
      </div>
    </div>
  );
};

export default ThresholdEditor;
